import React from 'react';
import {
  Instagram, Heart, MessageCircle, Send, Bookmark, MoreHorizontal,
  ArrowUpRight, Store, ChefHat, BadgePercent, Users, Sparkles,
  HeartHandshake, Truck, Award,
} from 'lucide-react';
import useReveal from '../hooks/useReveal';
import markImg from '../assets/brand/logo-mark.png';

const iconMap = {
  Store, ChefHat, BadgePercent, Users, Sparkles, HeartHandshake, Truck, Award,
};

/**
 * Bento-style Instagram-post tiles.
 * Each tile is styled as a mini IG post:
 *   header (profile + handle + more) → content → engagement bar → caption
 */
const posts = [
  {
    id: 1,
    icon: 'Store',
    tag: 'Store Tour',
    caption: 'Step inside our Adyar showroom — brands, kitchens & living, all in one place.',
    likes: '2.1k',
    comments: 48,
    timeAgo: '2d',
    span: 'lg:col-span-2 lg:row-span-2', // FEATURED 2x2
    theme: 'dark',
  },
  {
    id: 2,
    icon: 'ChefHat',
    tag: 'Live Demo',
    caption: 'Faber Hobtop Utopia in action — smoky flavours, effortless cook.',
    likes: '1.4k',
    comments: 22,
    timeAgo: '3d',
    span: '',
    theme: 'orange',
  },
  {
    id: 3,
    icon: 'BadgePercent',
    tag: 'Offer',
    caption: 'Festive edit → up to 30% off select cookware this week only.',
    likes: '3.6k',
    comments: 84,
    timeAgo: '4d',
    span: '',
    theme: 'sand',
  },
  {
    id: 4,
    icon: 'Users',
    tag: 'Community',
    caption: 'Meet the Iyer family — proud new owners of a Faber HT chimney.',
    likes: '2.8k',
    comments: 61,
    timeAgo: '5d',
    span: 'lg:col-span-2', // WIDE 2x1
    theme: 'light',
  },
  {
    id: 5,
    icon: 'Sparkles',
    tag: 'New Arrivals',
    caption: 'Fresh picks in appliances & lifestyle — swipe to see the drop.',
    likes: '1.9k',
    comments: 33,
    timeAgo: '6d',
    span: '',
    theme: 'orange',
  },
  {
    id: 6,
    icon: 'HeartHandshake',
    tag: 'Team',
    caption: 'The team that keeps every home happy — behind the counter.',
    likes: '2.3k',
    comments: 40,
    timeAgo: '1w',
    span: '',
    theme: 'dark',
  },
  {
    id: 7,
    icon: 'Truck',
    tag: 'Delivery',
    caption: 'Installed and demoed at your doorstep — hassle-free.',
    likes: '1.1k',
    comments: 18,
    timeAgo: '1w',
    span: '',
    theme: 'light',
  },
  {
    id: 8,
    icon: 'Award',
    tag: 'Trust',
    caption: 'Authorised dealer badge — genuine products with full warranty.',
    likes: '980',
    comments: 12,
    timeAgo: '2w',
    span: '',
    theme: 'sand',
  },
];

const themeMap = {
  dark: { bg: '#1a1410', fg: '#F7F4EA', sub: '#8E8E8E', accent: '#F58220', border: '#2a2622' },
  orange: { bg: '#F58220', fg: '#FFFFFF', sub: '#FFE7D2', accent: '#FFFFFF', border: '#C7580A' },
  sand: { bg: '#E9D6C1', fg: '#1a1410', sub: '#4D4D4D', accent: '#F58220', border: '#d6c0a5' },
  light: { bg: '#FFFFFF', fg: '#1a1410', sub: '#8E8E8E', accent: '#F58220', border: '#E9D6C1' },
};

const DotPattern = ({ color, opacity = 0.1 }) => (
  <svg className="absolute inset-0 w-full h-full pointer-events-none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ opacity }}>
    <defs>
      <pattern id={`ex-${color.replace('#', '')}`} width="18" height="18" patternUnits="userSpaceOnUse">
        <circle cx="2" cy="2" r="1.4" fill={color} />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill={`url(#ex-${color.replace('#', '')})`} />
  </svg>
);

const PostTile = ({ post, index }) => {
  const t = themeMap[post.theme];
  const Icon = iconMap[post.icon] || Sparkles;
  const isFeatured = post.span.includes('row-span-2');

  return (
    <a
      href="https://www.instagram.com/veetusaama/"
      target="_blank"
      rel="noreferrer"
      className={`reveal group relative rounded-2xl overflow-hidden border card-lift flex flex-col ${post.span}`}
      style={{
        background: t.bg,
        color: t.fg,
        borderColor: t.border,
        transitionDelay: `${index * 50}ms`,
      }}
    >
      {/* IG Post Header */}
      <div
        className="flex items-center gap-2 px-3 py-2.5 border-b relative z-10"
        style={{ borderColor: `${t.border}` }}
      >
        <div
          className="w-7 h-7 rounded-full p-[1.5px]"
          style={{
            background: 'linear-gradient(135deg,#F58220 0%,#C7580A 60%,#1a1410 100%)',
          }}
        >
          <div className="w-full h-full rounded-full flex items-center justify-center" style={{ background: t.bg }}>
            <img src={markImg} alt="" className="w-4 h-4 object-contain" />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-semibold truncate" style={{ color: t.fg }}>
            veetusaama
          </div>
          <div className="text-[9px] uppercase tracking-[0.14em] font-semibold truncate" style={{ color: t.sub }}>
            {post.tag} • {post.timeAgo}
          </div>
        </div>
        <MoreHorizontal className="w-4 h-4" style={{ color: t.sub }} strokeWidth={2} />
      </div>

      {/* Media area */}
      <div className="relative flex-1 flex items-center justify-center overflow-hidden min-h-[140px]">
        <DotPattern color={t.accent} opacity={t.bg === '#FFFFFF' ? 0.06 : 0.14} />
        {/* Halo rings */}
        <div
          className={`absolute rounded-full border ${isFeatured ? 'w-64 h-64' : 'w-40 h-40'}`}
          style={{ borderColor: `${t.accent}22` }}
        />
        <div
          className={`absolute rounded-full border ${isFeatured ? 'w-44 h-44' : 'w-28 h-28'}`}
          style={{ borderColor: `${t.accent}33` }}
        />
        {/* Icon */}
        <div
          className={`relative rounded-2xl flex items-center justify-center transition-transform duration-700 group-hover:scale-110 group-hover:rotate-3 shadow-lg ${
            isFeatured ? 'w-24 h-24 md:w-32 md:h-32' : 'w-16 h-16 md:w-20 md:h-20'
          }`}
          style={{
            background: t.theme === 'orange' ? '#FFFFFF' : t.accent,
          }}
        >
          <Icon
            className={isFeatured ? 'w-12 h-12 md:w-16 md:h-16' : 'w-8 h-8 md:w-10 md:h-10'}
            strokeWidth={1.4}
            style={{
              color: t.accent === '#F58220' ? '#FFFFFF' : t.bg,
            }}
          />
        </div>

        {/* View on IG badge on hover */}
        <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
          <div
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.18em] font-bold shadow-lg"
            style={{ background: t.fg, color: t.bg }}
          >
            <Instagram className="w-3 h-3" strokeWidth={2} />
            View
          </div>
        </div>
      </div>

      {/* Engagement bar */}
      <div className="px-3 pt-2.5 pb-1.5 flex items-center justify-between relative z-10" style={{ borderTop: `1px solid ${t.border}` }}>
        <div className="flex items-center gap-3">
          <Heart className="w-4 h-4 group-hover:fill-[#F58220] group-hover:text-[#F58220] transition-colors duration-300" style={{ color: t.fg }} strokeWidth={1.8} />
          <MessageCircle className="w-4 h-4" style={{ color: t.fg }} strokeWidth={1.8} />
          <Send className="w-4 h-4" style={{ color: t.fg }} strokeWidth={1.8} />
        </div>
        <Bookmark className="w-4 h-4" style={{ color: t.fg }} strokeWidth={1.8} />
      </div>

      {/* Caption */}
      <div className="px-3 pb-3 relative z-10">
        <div className="text-[11px] font-semibold" style={{ color: t.fg }}>
          {post.likes} likes
        </div>
        <div className="text-[11.5px] leading-[1.45] mt-1" style={{ color: t.fg }}>
          <span className="font-semibold">veetusaama</span>{' '}
          <span style={{ opacity: 0.9 }}>{post.caption}</span>
        </div>
        <div className="text-[10px] mt-1" style={{ color: t.sub }}>
          View all {post.comments} comments
        </div>
      </div>
    </a>
  );
};

const StoreExperience = () => {
  useReveal();

  return (
    <section id="experience" className="py-16 lg:py-24 paper-bg">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header row */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
          <div className="reveal">
            <div className="text-[11px] uppercase tracking-[0.32em] font-semibold text-[#F58220] mb-3 flex items-center gap-2">
              <Instagram className="w-4 h-4" /> Store Experience
            </div>
            <h2 className="h-display uppercase text-[36px] md:text-[52px] leading-[1.02] text-[#1a1410]">
              Moments <span className="text-[#F58220]">from our store</span>
            </h2>
            <p className="mt-4 text-[14.5px] leading-[1.7] text-[#4D4D4D] max-w-[560px]">
              Store tours, live demos, festive offers and the families we serve.
              Follow along on Instagram.
            </p>
          </div>

          <div className="reveal flex items-center gap-4">
            <a
              href="https://www.instagram.com/veetusaama/"
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-3 group"
            >
              <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform duration-500"
                style={{
                  background: 'linear-gradient(135deg,#F58220 0%,#C7580A 60%,#1a1410 100%)',
                }}
              >
                <Instagram className="w-5 h-5 text-white" strokeWidth={1.9} />
              </div>
              <div>
                <div className="h-display uppercase text-[16px] text-[#1a1410] leading-none">
                  @veetusaama
                </div>
                <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#4D4D4D] mt-1.5">
                  Follow on Instagram
                </div>
              </div>
            </a>
            <a
              href="https://www.instagram.com/veetusaama/"
              target="_blank"
              rel="noreferrer"
              className="btn-primary hidden md:flex px-5 py-3 rounded-full text-[10px] uppercase tracking-[0.24em] items-center gap-2 whitespace-nowrap"
            >
              Visit Profile
              <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>

        {/* Bento asymmetric grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 auto-rows-[minmax(240px,auto)]">
          {posts.map((p, i) => (
            <PostTile key={p.id} post={p} index={i} />
          ))}
        </div>

        {/* Footer strip */}
        <div className="reveal mt-8 rounded-2xl overflow-hidden">
          <a
            href="https://www.instagram.com/veetusaama/"
            target="_blank"
            rel="noreferrer"
            className="flex flex-wrap items-center justify-between gap-4 p-6 md:p-7 bg-[#1a1410] text-[#F7F4EA] hover:bg-[#0f0d0b] transition-colors duration-400"
          >
            <div className="flex items-center gap-4">
              <div
                className="w-11 h-11 rounded-2xl flex items-center justify-center shadow-md"
                style={{ background: 'linear-gradient(135deg,#F58220 0%,#C7580A 60%,#1a1410 100%)' }}
              >
                <Instagram className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] font-semibold text-[#F58220]">
                  Stay Connected
                </div>
                <div className="h-display uppercase text-[18px] md:text-[24px] leading-tight">
                  See more • <span className="text-[#F58220]">@veetusaama</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="font-script text-[22px] text-[#F58220] hidden md:block">For a Better Tomorrow</div>
              <div className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#F58220] hover:bg-[#C7580A] text-white text-[10px] uppercase tracking-[0.26em] font-bold transition-colors duration-400">
                Follow <ArrowUpRight className="w-4 h-4" />
              </div>
            </div>
          </a>
        </div>
      </div>
    </section>
  );
};

export default StoreExperience;
